import { FormGroup } from '@angular/forms';

/**
 * Patches an address form group with data from an address object
 *
 * @param formGroup The form group to patch (can be nested, e.g. form.get('address'))
 * @param addressData The address data object from the API
 * @param formFieldPath Optional path to the address form group if it's nested within another form group
 * @returns void
 */
export function patchAddress(formGroup: FormGroup | null, addressData: any, formFieldPath?: string): void {
  if (!formGroup || !addressData) return;

  /** Special case for Danish API which uses 'dør' as property name */
  const doorReference = 'dør';

  // Handle three formats:
  // 1. adresser/{id} response: nested via addressData.adgangsadresse
  // 2. adgangsadresser/{id} response: nested directly on root (vejstykke, postnummer, kommune)
  // 3. autocomplete flat format: flat field names (vejnavn, postnr, etc.)
  const isFullAddressFormat = !!addressData.adgangsadresse;
  const isAccessAddressFormat = !isFullAddressFormat && !!addressData.vejstykke;

  const streetName = isFullAddressFormat
    ? addressData.adgangsadresse?.vejstykke?.navn
    : isAccessAddressFormat
      ? addressData.vejstykke?.navn
      : addressData.vejnavn;
  const streetCode = isFullAddressFormat
    ? addressData.adgangsadresse?.vejstykke?.kode
    : isAccessAddressFormat
      ? addressData.vejstykke?.kode
      : addressData.vejkode;
  const buildingNumber = isFullAddressFormat
    ? addressData.adgangsadresse?.husnr
    : addressData.husnr;
  const floorIdentification = addressData.etage;
  const roomIdentification = addressData[doorReference];
  const subDivisionName = isFullAddressFormat
    ? addressData.adgangsadresse?.supplerendebynavn
    : isAccessAddressFormat
      ? addressData.supplerendebynavn
      : (addressData.supplerendebynavn ?? addressData.subDivisionName);
  const postalCode = isFullAddressFormat
    ? addressData.adgangsadresse?.postnummer?.nr
    : isAccessAddressFormat
      ? addressData.postnummer?.nr
      : addressData.postnr;
  const cityName = isFullAddressFormat
    ? addressData.adgangsadresse?.postnummer?.navn
    : isAccessAddressFormat
      ? addressData.postnummer?.navn
      : addressData.postnrnavn;
  const municipalityCode = isFullAddressFormat
    ? addressData.adgangsadresse?.kommune?.kode
    : isAccessAddressFormat
      ? addressData.kommune?.kode
      : addressData.kommunekode;

  const targetGroup = formFieldPath ? formGroup.get(formFieldPath) as FormGroup : formGroup;

  if (!targetGroup) return;

  targetGroup.patchValue({
    id: addressData.id,
    streetName,
    streetCode,
    buildingNumber,
    floorIdentification,
    roomIdentification,
    subDivisionName,
    postalCode,
    cityName,
    municipalityCode,
    washInstructions: addressData.washInstructions,
    darReference: addressData.id,
    country: 'DK'
  });
}
